import { useNode, useEditor } from "@craftjs/core";
import React, { useContext } from "react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { GrigoraEditorContext } from "../../editor/context";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RadioButtonInput,
  IconInput,
  NumberInput,
  RangeNumberInput,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const NavigationSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const {
    menuTrigger = "tablet",
    menuStatus = "hide",
    menuIcon = "list",
    menuIconSize = "26px",
    menuIconBorder,
    menuIconBorderRadius,
    menuIconPadding = {
      top: "8px",
      bottom: "8px",
      right: "8px",
      left: "8px",
    },
    menuIconColor,
    menuIconBackgroundColor,
    menuDirection = "row",
    menuWrap = "wrap",
    menuJustifyContent = "start",
    menuAlignItems = "center",
    menuGap = "1rem",
    portalPadding = {
      top: "20px",
      bottom: "20px",
      left: "20px",
      right: "20px",
    },
    portalGap = "1rem",
    portalType = "dropdown",
    portalTopOffset,
    portalWidth = "100vw",
    portalHeight,
    portalAlign,
    portalSelfAlign,
    portalCloseIconColor,
    portalCloseIconAlign,
    portalColor,
    portalBackgroundColor = "#E8E8E8",
    hoverPortalColor,
    hoverPortalBackgroundColor,
    hoverPortalCloseIconColor,
    hoverMenuIconBorder,
    hoverMenuIconBorderRadius,
    hoverMenuIconColor,
    hoverMenuIconBackgroundColor,
    transition,
  } = props;

  const DIRECTION = [
    {
      label: "Column",
      value: "column",
    },
    {
      label: "Row",
      value: "row",
    },
  ];

  const WRAP = [
    {
      label: "Wrap",
      value: "wrap",
    },
    {
      label: "Nowrap",
      value: "nowrap",
    },
  ];

  const JUSTIFYCONTENT = [
    {
      label: "Start",
      value: "start",
    },
    {
      label: "Center",
      value: "center",
    },
    {
      label: "End",
      value: "end",
    },
    {
      label: "Space-between",
      value: "space-between",
    },
    {
      label: "Space-around",
      value: "space-around",
    },
    {
      label: "Space-evenly",
      value: "space-evenly",
    },
  ];

  const ALIGNITEMS = [
    {
      label: "Start",
      value: "start",
    },
    {
      label: "Center",
      value: "center",
    },
    {
      label: "End",
      value: "end",
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            isResetVisible={false}
            value={menuTrigger}
            onChange={(val) => {
              setProp((props) => {
                props.menuTrigger = val;
              });
            }}
            options={[
              {
                label: "Desktop or lower",
                value: "desktop",
              },
              {
                label: "Tablet or lower",
                value: "tablet",
              },
              {
                label: "Mobile",
                value: "mobile",
              },
            ]}
            label="Menu Icon Trigger"
          />
          <RadioButtonInput
            isResetVisible={false}
            value={menuStatus}
            onChange={(val) => {
              setProp((props) => {
                props.menuStatus = val;
              });
            }}
            options={[
              {
                label: "Show",
                value: "show",
              },
              {
                label: "Hide",
                value: "hide",
              },
            ]}
            label="Menu"
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: menuDirection, default: "row" },
            { key: menuWrap, default: "wrap" },
            { key: menuAlignItems, default: "center" },
            { key: menuJustifyContent, default: "start" },
            { key: menuGap, default: "1rem" },
          ])}
          id={`block-setting-${id}-menu-non-collapsed`}
          title={"Menu (Non-Collapsed)"}
          initialOpen={false}
        >
          <>
            <SelectInput
              resetValue={"row"}
              value={menuDirection}
              onChange={(val) => {
                setProp((props) => {
                  props.menuDirection = val;
                });
              }}
              options={DIRECTION}
              label="Direction"
            />
            <SelectInput
              resetValue={"wrap"}
              value={menuWrap}
              onChange={(val) => {
                setProp((props) => {
                  props.menuWrap = val;
                });
              }}
              options={WRAP}
              label="Wrap"
            />
            {menuDirection === "column" ? (
              <>
                <SelectInput
                  resetValue={"start"}
                  value={menuAlignItems}
                  onChange={(val) => {
                    setProp((props) => {
                      props.menuAlignItems = val;
                    });
                  }}
                  options={ALIGNITEMS}
                  label="Align"
                />
                <SelectInput
                  resetValue={"start"}
                  value={menuJustifyContent}
                  onChange={(val) => {
                    setProp((props) => {
                      props.menuJustifyContent = val;
                    });
                  }}
                  options={JUSTIFYCONTENT}
                  label="Vertical Align"
                />
              </>
            ) : (
              <>
                <SelectInput
                  resetValue={"start"}
                  value={menuJustifyContent}
                  onChange={(val) => {
                    setProp((props) => {
                      props.menuJustifyContent = val;
                    });
                  }}
                  options={JUSTIFYCONTENT}
                  label="Align"
                />
                <SelectInput
                  resetValue={"center"}
                  value={menuAlignItems}
                  onChange={(val) => {
                    setProp((props) => {
                      props.menuAlignItems = val;
                    });
                  }}
                  options={ALIGNITEMS}
                  label="Vertical Align"
                />
              </>
            )}
            <UnitInput
              resetValue={"1rem"}
              value={menuGap}
              onChange={(val) => {
                setProp((props) => {
                  props.menuGap = val;
                });
              }}
              label={"Gap"}
            />
          </>
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: portalType, default: "dropdown" },
            { key: portalWidth, default: "100vw" },
            { key: portalHeight },
            { key: portalTopOffset },
            { key: portalSelfAlign },
            { key: portalAlign },
            { key: portalCloseIconAlign },
            {
              key: portalPadding,
              default: {
                top: "20px",
                bottom: "20px",
                left: "20px",
                right: "20px",
              },
            },
            { key: portalGap, default: "1rem" },
          ])}
          id={`block-setting-${id}-menu-collapsed`}
          title={"Menu (Collapsed)"}
          initialOpen={false}
        >
          <SelectInput
            resetValue={"dropdown"}
            value={portalType}
            onChange={(val) => {
              setProp((props) => {
                props.portalType = val;
              });
              if (val === "dropdown") {
                setProp((props) => {
                  props.portalWidth = "100vw";
                  props.portalHeight = undefined;
                  props.portalSelfAlign = undefined;
                  props.portalAlign = undefined;
                });
              }
              if (val === "screen") {
                setProp((props) => {
                  props.portalWidth = "100vw";
                  props.portalHeight = "100vh";
                  props.portalSelfAlign = undefined;
                  props.portalAlign = undefined;
                  props.portalCloseIconAlign = undefined;
                });
              }
            }}
            options={[
              {
                value: "dropdown",
                label: "Dropdown",
              },
              {
                value: "screen",
                label: "Screen",
              },
            ]}
            label="Type"
          />
          <UnitInput
            resetValue={"100vw"}
            value={portalWidth}
            onChange={(val) => {
              setProp((props) => {
                props.portalWidth = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            auto
            value={portalHeight}
            onChange={(val) => {
              setProp((props) => {
                props.portalHeight = val;
              });
            }}
            label={"Height"}
          />
          <NumberInput
            min={INPUT_LIMITS["navigation-top-offset"].min}
            max={INPUT_LIMITS["navigation-top-offset"].max}
            value={portalTopOffset}
            onChange={(val) => {
              setProp((props) => {
                props.portalTopOffset = val;
              });
            }}
            label={"Top Offset"}
          />
          <AlignInput
            value={portalSelfAlign}
            onChange={(val) => {
              setProp((props) => {
                props.portalSelfAlign = val;
              });
            }}
            label={"Align Collapsed Container"}
          />
          <AlignInput
            value={portalAlign}
            onChange={(val) => {
              setProp((props) => {
                props.portalAlign = val;
              });
            }}
            label={"Align Content"}
          />
          {portalType === "screen" && (
            <AlignInput
              value={portalCloseIconAlign}
              onChange={(val) => {
                setProp((props) => {
                  props.portalCloseIconAlign = val;
                });
              }}
              label={"Close Icon Align"}
            />
          )}

          <UnitInput
            resetValue={"1rem"}
            value={portalGap}
            onChange={(val) => {
              setProp((props) => {
                props.portalGap = val;
              });
            }}
            label={"Gap"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: menuIcon, default: "list" },
            { key: menuIconSize, default: "26px" },
            { key: menuIconBorder },
            { key: menuIconBorderRadius },
            {
              key: menuIconPadding,
              default: {
                top: "8px",
                bottom: "8px",
                right: "8px",
                left: "8px",
              },
            },
          ])}
          id={`block-setting-${id}-menu-icon`}
          title={"Menu Icon"}
          initialOpen={false}
        >
          <IconInput
            resetValue={undefined}
            activeIcon={menuIcon}
            setActiveIcon={(val) => {
              setProp((props) => {
                props.menuIcon = val;
              });
            }}
            hideRemoveButton={true}
          />
          <UnitInput
            resetValue={"20px"}
            value={menuIconSize}
            onChange={(val) => {
              setProp((props) => {
                props.menuIconSize = val;
              });
            }}
            label={"Size"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const menuIconColorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleMenuIconColorStates({
          menuIconPropKey: "menuIconColor",
          menuIconProp: menuIconColor,
          menuIconBackgroundColorPropKey: "menuIconBackgroundColor",
          menuIconBackgroundColorProp: menuIconBackgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleMenuIconColorStates({
          menuIconPropKey: "hoverMenuIconColor",
          menuIconProp: hoverMenuIconColor,
          menuIconBackgroundColorPropKey: "hoverMenuIconBackgroundColor",
          menuIconBackgroundColorProp: hoverMenuIconBackgroundColor,
        }),
      },
    ];

    function handleMenuIconColorStates({
      menuIconPropKey,
      menuIconProp,
      menuIconBackgroundColorPropKey,
      menuIconBackgroundColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={menuIconProp}
            onChange={(val) => {
              setProp((props) => {
                props[menuIconPropKey] = val;
              });
            }}
            label={"Color"}
          />
          <ColorInput
            resetValue={undefined}
            value={menuIconBackgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[menuIconBackgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(menuIconPropKey === "hoverMenuIconColor" ||
            menuIconBackgroundColorPropKey ===
              "hoverMenuIconBackgroundColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    const menuCollapsedlorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          portalColorPropKey: "portalColor",
          portalColorProp: portalColor,
          portalBackgroundColorPropKey: "portalBackgroundColor",
          portalBackgroundColorProp: portalBackgroundColor,
          portalCloseIconColorPropKey: "portalCloseIconColor",
          portalCloseIconColorProp: portalCloseIconColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          portalColorPropKey: "hoverPortalColor",
          portalColorProp: hoverPortalColor,
          portalBackgroundColorPropKey: "hoverPortalBackgroundColor",
          portalBackgroundColorProp: hoverPortalBackgroundColor,
          portalCloseIconColorPropKey: "hoverPortalCloseIconColor",
          portalCloseIconColorProp: hoverPortalCloseIconColor,
        }),
      },
    ];

    function handleColorStates({
      portalColorPropKey,
      portalColorProp,
      portalBackgroundColorPropKey,
      portalBackgroundColorProp,
      portalCloseIconColorPropKey,
      portalCloseIconColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={portalColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[portalColorPropKey] = val;
              });
            }}
            label={"Color"}
          />
          {portalType === "screen" && (
            <ColorInput
              resetValue={undefined}
              value={portalCloseIconColorProp}
              onChange={(val) => {
                setProp((props) => {
                  props[portalCloseIconColorPropKey] = val;
                });
              }}
              label={"Close Icon"}
            />
          )}
          <ColorInput
            resetValue={
              portalBackgroundColorPropKey === "portalBackgroundColor"
                ? "#E8E8E8"
                : undefined
            }
            value={portalBackgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[portalBackgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />

          {(portalBackgroundColorPropKey === "hoverPortalBackgroundColor" ||
            portalCloseIconColorPropKey === "hoverPortalCloseIconColor" ||
            portalColorPropKey === "hoverPortalColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: menuIconColor },
              { key: menuIconBackgroundColor },
              { key: hoverMenuIconColor },
              { key: hoverMenuIconBackgroundColor },
            ])}
            id={`block-setting-${id}-menu-icon`}
            title="Menu Icon"
          >
            <TabsV2 items={menuIconColorTabs} />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: portalColor },
              { key: portalCloseIconColor },
              { key: portalBackgroundColor, default: "#E8E8E8" },
              { key: hoverPortalColor },
              { key: hoverPortalCloseIconColor },
              { key: hoverPortalBackgroundColor },
            ])}
            id={`block-setting-${id}-menu-collapsed`}
            title="Menu (Collapsed)"
          >
            <TabsV2 items={menuCollapsedlorTabs} />
          </Panel>
        </div>
      </>
    );
  }
  function AdvancedSettings() {
    const menuIconBorderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderRadiusStates({
          menuIconBorderPropKey: "menuIconBorder",
          menuIconBorderProp: menuIconBorder,
          menuIconBorderRadiusPropKey: "menuIconBorderRadius",
          menuIconBorderRadiusProp: menuIconBorderRadius,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderRadiusStates({
          menuIconBorderPropKey: "hoverMenuIconBorder",
          menuIconBorderProp: hoverMenuIconBorder,
          menuIconBorderRadiusPropKey: "hoverMenuIconBorderRadius",
          menuIconBorderRadiusProp: hoverMenuIconBorderRadius,
        }),
      },
    ];

    function handleBorderRadiusStates({
      menuIconBorderPropKey,
      menuIconBorderProp,
      menuIconBorderRadiusPropKey,
      menuIconBorderRadiusProp,
    }) {
      return (
        <>
          <BorderInput
            value={menuIconBorderProp}
            onChange={(val) => {
              setProp((props) => {
                props[menuIconBorderPropKey] = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={menuIconBorderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[menuIconBorderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />
          {(menuIconBorderRadiusPropKey === "hoverMenuIconBorderRadius" ||
            menuIconBorderPropKey === "hoverMenuIconBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.[("border", "border-radius")]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border", "border-radius"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        {portalType === "screen" && (
          <Panel
            isUpdated={isUpdatedFromDefault([
              {
                key: portalPadding,
                default: {
                  top: "20px",
                  bottom: "20px",
                  left: "20px",
                  right: "20px",
                },
              },
            ])}
            id={`block-setting-${id}-menu-collapsed`}
            title={"Menu(Collapsed) Layout"}
            initialOpen={false}
          >
            <PaddingInput
              resetValue={{
                top: "20px",
                bottom: "20px",
                left: "20px",
                right: "20px",
              }}
              value={portalPadding}
              onChange={(val) => {
                setProp((props) => {
                  props.portalPadding = val;
                });
              }}
              label={"Padding"}
            />
          </Panel>
        )}
        <Panel
          isUpdated={isUpdatedFromDefault([
            {
              key: menuIconPadding,
              default: {
                top: "8px",
                bottom: "8px",
                right: "8px",
                left: "8px",
              },
            },
          ])}
          id={`block-setting-${id}-menu-collapsed`}
          title={"Menu Icon Layout"}
          initialOpen={false}
        >
          <PaddingInput
            resetValue={{
              top: "8px",
              bottom: "8px",
              right: "8px",
              left: "8px",
            }}
            value={menuIconPadding}
            onChange={(val) => {
              setProp((props) => {
                props.menuIconPadding = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: menuIconBorder },
            { key: menuIconBorderRadius },
            { key: hoverMenuIconBorder },
            { key: hoverMenuIconBorderRadius },
          ])}
          id={`block-setting-${id}-menu-collapsed`}
          title={"Menu Icon Layout"}
          initialOpen={false}
        >
          <TabsV2 items={menuIconBorderTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: menuTrigger },
        { key: menuStatus },
        { key: menuDirection, default: "row" },
        { key: menuWrap, default: "wrap" },
        { key: menuAlignItems, default: "center" },
        { key: menuJustifyContent, default: "start" },
        { key: menuGap, default: "1rem" },
        { key: portalType, default: "dropdown" },
        { key: portalWidth, default: "100vw" },
        { key: portalHeight },
        { key: portalTopOffset },
        { key: portalSelfAlign },
        { key: portalAlign },
        { key: portalCloseIconAlign },
        { key: portalGap, default: "1rem" },
        { key: menuIcon, default: "list" },
        { key: menuIconSize, default: "26px" },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: menuIconColor },
        { key: menuIconBackgroundColor },
        { key: portalColor },
        { key: portalCloseIconColor },
        { key: portalBackgroundColor, default: "#E8E8E8" },
        { key: hoverMenuIconColor },
        { key: hoverMenuIconBackgroundColor },
        { key: hoverPortalColor },
        { key: hoverPortalCloseIconColor },
        { key: hoverPortalBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: menuIconBorder },
        { key: menuIconBorderRadius },
        { key: hoverMenuIconBorder },
        { key: hoverMenuIconBorderRadius },
        { key: menuIconBorder },
        { key: menuIconBorderRadius },
        { key: hoverMenuIconBorder },
        { key: hoverMenuIconBorderRadius },
        {
          key: portalPadding,
          default: {
            top: "20px",
            bottom: "20px",
            left: "20px",
            right: "20px",
          },
        },
        {
          key: menuIconPadding,
          default: {
            top: "8px",
            bottom: "8px",
            right: "8px",
            left: "8px",
          },
        },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
